"use client";

import { useEffect, useState } from "react";
import { createTheme, MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { PostHogAnalyticsProvider } from "@/contexts/PostHogAnalyticsContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { AppCompatRouterProvider } from "@/lib/compat-router/AppProvider";
import { AppPopupManagerProvider } from "@/contexts/AppPopupManagerContext";
import { APP_POPUP_POOL } from "@/contexts/config/app_popup_data_pool";
import { CookieBanner } from "@/components/cookies/CookieBanner";
import { PersonalizationNudge } from "@/components/cookies/PersonalizationNudge";
const theme = createTheme({
  fontFamily: "var(--font-jakarta)",
});

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient());

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      process.env.NODE_ENV === "production"
    ) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  // New service worker available
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  return (
    <CookieConsentProvider>
      <AppCompatRouterProvider>
        <LocationProvider>
          <AuthProvider>
            <PostHogAnalyticsProvider>
              <GoogleOAuthProvider
                clientId={process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID as string}
              >
                <QueryClientProvider client={queryClient}>
                  <CurrencyProvider>
                    <MantineProvider theme={theme}>
                      <ToastContainer position="top-right" theme="colored" />
                      <AppPopupManagerProvider popupData={APP_POPUP_POOL}>
                        {children}
                        <CookieBanner />
                        <PersonalizationNudge />
                      </AppPopupManagerProvider>
                    </MantineProvider>
                  </CurrencyProvider>
                </QueryClientProvider>
              </GoogleOAuthProvider>
            </PostHogAnalyticsProvider>
          </AuthProvider>
        </LocationProvider>
      </AppCompatRouterProvider>
    </CookieConsentProvider>
  );
}
